import { useCallback, useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { FileSpreadsheet, FileText, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { MonthField } from "@/components/DateField";
import { Panel } from "@/components/KpiCard";
import { RelancesHistorique } from "@/components/RelancesHistorique";
import { EnvoisAttente } from "@/components/EnvoisAttente";
import { api, download, errMsg, money } from "@/lib/api";

const moisCourant = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

const Lettre = ({ l, ton, label, onDone }) => {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { await download("/loyers-manquants/lettre", { locataire_id: l.locataire_id, mois: l.mois, ton }, `${label} ${l.mois}.pdf`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" size="sm" className={`h-7 px-2 text-[11px] ${ton === "ferme" ? "text-red-700 border-red-200" : ""}`} disabled={busy} onClick={dl} data-testid={`lettre-${ton}-${l.mois}-${l.locataire_id}`}>
      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}{label}
    </Button>
  );
};

const Envoi = ({ l, ton, label, onDone }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!window.confirm(`Envoyer « ${label} » à ${l.locataire} (${l.courriel}) ? Une copie vous sera envoyée.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/loyers-manquants/lettre/envoyer", { locataire_id: l.locataire_id, mois: l.mois, ton }); toast.success(`Lettre envoyée à ${data.envoye_a}`); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" size="icon" className="h-7 w-7" disabled={busy || !l.courriel} onClick={send}
      title={l.courriel ? `Envoyer « ${label} » par courriel` : "Ajoutez le courriel du locataire dans Locataires mémorisés"} data-testid={`envoi-${ton}-${l.mois}-${l.locataire_id}`}>
      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
    </Button>
  );
};

const TONS = { courtois: "Rappel", ferme: "Mise en demeure" };

const Ligne = ({ l, i, onDone }) => (
  <tr className="border-b border-slate-100" data-testid={`loyer-manquant-${i}`}>
    <td className="py-2 px-2 font-mono text-xs">{l.mois}</td>
    <td className="py-2 px-2">{l.locataire}</td>
    <td className="py-2 px-2 text-slate-600">{l.logement}</td>
    <td className="py-2 px-2"><span className={`rounded px-1.5 py-0.5 text-[11px] ${l.recu ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`}>{l.statut}</span></td>
    <td className="py-2 px-2 text-right font-mono text-xs">{money(l.attendu)}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{money(l.recu)}</td>
    <td className="py-2 px-2 text-right font-mono text-xs text-red-700">{money(l.manque)}</td>
    <td className="py-2 px-2 font-mono text-xs text-slate-500">{l.dernier_depot || "—"}</td>
    <td className="py-2 px-2"><div className="flex gap-1.5 justify-end"><Lettre l={l} ton="courtois" label="Rappel" onDone={onDone} /><Envoi l={l} ton="courtois" label="Rappel" onDone={onDone} /><Lettre l={l} ton="ferme" label="Mise en demeure" onDone={onDone} /><Envoi l={l} ton="ferme" label="Mise en demeure" onDone={onDone} /></div>
      {l.relances?.length > 0 && <p className="mt-1 text-right text-[10px] font-mono text-slate-500" data-testid={`loyer-manquant-relances-${i}`}>Relancé : {l.relances.map((r) => `${TONS[r.ton]} ${r.date}`).join(" · ")}</p>}
    </td>
  </tr>
);

const Tableau = ({ lignes, onDone }) => (
  <div className="overflow-x-auto">
    <SmartTable id="loyers-manquants" className="w-full text-sm" data-testid="loyers-manquants-table">
      <thead><tr className="border-b border-slate-200 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
        <th className="py-2 px-2">Mois</th><th className="py-2 px-2">Locataire</th><th className="py-2 px-2">Logement</th><th className="py-2 px-2">Statut</th>
        <th className="py-2 px-2 text-right">Loyer habituel</th><th className="py-2 px-2 text-right">Reçu</th><th className="py-2 px-2 text-right">Manquant</th><th className="py-2 px-2">Dernier dépôt</th><th className="py-2 px-2 text-right">Lettre au locataire</th>
      </tr></thead>
      <tbody>{lignes.map((l, i) => <Ligne key={`${l.mois}-${l.locataire_id}`} l={l} i={i} onDone={onDone} />)}</tbody>
    </SmartTable>
  </div>
);

export const LoyersManquantsPanel = ({ version }) => {
  const [p, setP] = useState({ debut: moisCourant(), fin: moisCourant() });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [cale, setCale] = useState(false);
  useEffect(() => {
    if (cale || !data?.derniere_operation) return;
    setCale(true);
    const m = data.derniere_operation.slice(0, 7);
    if (m < p.fin) setP({ debut: m, fin: m });
  }, [data, cale, p.fin]);
  const [hist, setHist] = useState(0);
  const charger = useCallback(() => {
    if (!p.debut || !p.fin) return;
    api.get("/loyers-manquants", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [p]);
  const apresLettre = () => { charger(); setHist((h) => h + 1); };
  useEffect(() => { charger(); }, [charger, version]);
  const dl = async (format) => {
    setBusy(format);
    try { await download("/loyers-manquants/export", { format, ...p }, `Loyers manquants.${format}`); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <Panel title="Loyers manquants" testId="loyers-manquants-panel" action={
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => dl("xlsx")} disabled={!!busy} data-testid="loyers-manquants-xlsx">{busy === "xlsx" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />}Excel</Button>
        <Button variant="outline" size="sm" onClick={() => dl("pdf")} disabled={!!busy} data-testid="loyers-manquants-pdf">{busy === "pdf" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5" />}PDF</Button>
      </div>}>
      <div className="flex flex-wrap items-end gap-3 mb-3">
        <label className="text-xs text-slate-500 flex flex-col gap-1">Du mois<MonthField value={p.debut} onChange={(v) => { setCale(true); setP({ ...p, debut: v }); }} onPeriode={(r) => { setCale(true); setP({ ...p, ...r }); }} className="w-40" data-testid="loyers-manquants-debut" /></label>
        <label className="text-xs text-slate-500 flex flex-col gap-1">Au mois<MonthField value={p.fin} onChange={(v) => { setCale(true); setP({ ...p, fin: v }); }} onPeriode={(r) => { setCale(true); setP({ ...p, ...r }); }} className="w-40" data-testid="loyers-manquants-fin" /></label>
        {data && <p className="text-xs text-slate-500 pb-2" data-testid="loyers-manquants-resume">{data.locataires_suivis} locataire(s) suivi(s) · opérations importées jusqu'au {data.derniere_operation || "—"} · envoi automatique par courriel le 7 de chaque mois</p>}
      </div>
      {!data ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : data.lignes.length === 0
        ? <p className="text-sm text-emerald-700" data-testid="loyers-manquants-vide">Aucun loyer manquant sur la période.</p>
        : <Tableau lignes={data.lignes} onDone={apresLettre} />}
      <EnvoisAttente version={hist + version} onDone={apresLettre} />
      <RelancesHistorique version={hist + version} onChange={charger} />
    </Panel>
  );
};
